import type Phaser from 'phaser';
import type { MowerId } from '../../content/mowers';
import { MOWER_DEFS } from '../../content/mowers';
import { dx, dy } from '../../sim/direction';
import type { SimLevel } from '../../sim/level';
import { decodeIntent, hashSimState } from '../../sim/replay';
import { Simulation } from '../../sim/simulation';
import { safeTick, type FriendTrack } from '../raceView';
import { mowerPixelPosition } from './interpolate';
import { paintedMowerTexture } from './cosmeticArt';
import { mowerTextureKey } from './placeholderArt';

const GHOST_ALPHA = 0.55;
/** A friend is warmer than a recorded ghost's pale blue (when they wear no paint of their own). */
const FRIEND_TINT = 0xffd8a8;

/**
 * The friend in a live race (docs/LIVE.md): their mower replayed in a sim of its own from the turns
 * their phone sends, and drawn see-through on this player's lawn. It never runs past the last tick
 * the friend reported with every turn before it in hand (`safeTick`), so it trails them by the
 * lag. It mows only its own copy of the lawn. If its state stops matching the friend's (a sim bug), it hides and the
 * HUD keeps only their reported share.
 */
export class LiveGhost {
  readonly sim: Simulation;
  private next = 0;
  private readonly sprite: Phaser.GameObjects.Sprite;
  private readonly coinIndexes: number[];
  private readonly coinSeen: boolean[];
  /** The friend's state and the ghost's differed: the ghost is hidden from then on. */
  outOfSync = false;

  /** `paint`: the friend's own paint on this mower, shown on the ghost (null: their mower's colors). */
  constructor(
    scene: Phaser.Scene,
    level: SimLevel,
    mower: MowerId,
    depth: number,
    paint: string | null = null,
  ) {
    this.sim = new Simulation(level, MOWER_DEFS[mower]);
    this.sim.drainEvents();
    this.coinIndexes = (level.pickups ?? []).flatMap((p, i) => (p.kind === 'coin' ? [i] : []));
    this.coinSeen = this.coinIndexes.map(() => false);
    this.sprite = scene.add
      .sprite(
        0,
        0,
        paint ? paintedMowerTexture(scene, mower, paint) : mowerTextureKey(mower),
        this.sim.mower.dir,
      )
      .setAlpha(GHOST_ALPHA)
      .setDepth(depth);
    if (!paint) this.sprite.setTint(FRIEND_TINT);
    this.render(0);
  }

  /** The friend's mower has stopped: cleared, or out. */
  get done(): boolean {
    return this.sim.status !== 'running';
  }

  /**
   * Runs the ghost on to `target` (this player's tick), no further than the friend has reported.
   * `onCoin` hears each race coin it picks up: the coin's place among the lawn's coins, and the tick.
   */
  advance(target: number, track: FriendTrack, onCoin: (coin: number, tick: number) => void): void {
    const limit = Math.min(target, safeTick(track));
    while (this.sim.tick < limit && this.sim.status === 'running') {
      const tick = this.sim.tick + 1;
      for (
        let turn = track.inputs[this.next];
        turn && turn[0] <= tick;
        turn = track.inputs[this.next]
      ) {
        try {
          this.sim.enqueue(decodeIntent(turn[1]));
        } catch {
          this.outOfSync = true;
        }
        this.next++;
      }
      this.sim.step();
      this.sim.drainEvents();
      this.coinIndexes.forEach((index, coin) => {
        if (!this.coinSeen[coin] && this.sim.isCollected(index)) {
          this.coinSeen[coin] = true;
          onCoin(coin, this.sim.tick);
        }
      });
      if (!this.outOfSync) {
        const theirs = track.hashes.find(([t]) => t === this.sim.tick)?.[1];
        if (theirs !== undefined && theirs !== hashSimState(this.sim)) this.outOfSync = true;
      }
    }
  }

  render(alpha: number): void {
    const m = this.sim.mower;
    const willAdvance = !this.done && !this.sim.blocks(m.x + dx(m.dir), m.y + dy(m.dir));
    const { px, py } = mowerPixelPosition(
      m,
      this.sim.crossTicks(),
      this.done ? 0 : alpha,
      willAdvance,
    );
    this.sprite.setPosition(px, py).setFrame(m.dir);
    this.sprite.setVisible(!this.outOfSync).setAlpha(this.done ? GHOST_ALPHA / 2 : GHOST_ALPHA);
  }

  destroy(): void {
    this.sprite.destroy();
  }
}
